import {
  bridgeConflicts, deviceRows, symbolName, duplicateNumbers, LIB, netRows, normaliseTag, renameDevice, renumberStrip, schematicOrder, stripAccessories, terminalStrips,
  WIRE_COLOURS, WIRE_SECTIONS, loc, type Analysis, type DeviceRow, type Project, type Strip,
} from '@enercad/core';
import { esc, toast } from '../ui';
import { t as $t, tk } from '@enercad/core';


/*
 * Navigators (EPLAN style): terminal strip editor, connections, cables and a
 * device table with bulk editing. Everything edits the schematic in place
 * through the editor's undoable `mutate`.
 */

export interface NavHost {
  readonly pr: Project;
  readonly A: Analysis;
  readonly readOnly: boolean;
  mutate(fn: () => void): void;
  focusOn(pi: number, sid: string): void;
  focusPage(pi: number): void;
}

type Tab = 'ter' | 'con' | 'cab' | 'dev';
const TABS: [Tab, string][] = [['ter', tk('Klemensler')], ['con', tk("Bağlantılar")], ['cab', tk('Kablolar')], ['dev', tk('Cihazlar')]];

const DEV_FIELDS: [string, string][] = [['desc', tk("Açıklama")], ['part', tk("Parça no")], ['maker', tk("Üretici")], ['fn', tk('Fonksiyon (=)')], ['site', tk('Kurulum yeri (++)')], ['loc', tk('Konum (+)')], ['ud', tk('Kullanıcı tanımlı (#)')]];

export class NavView {
  private tab: Tab = 'ter';
  private strip = '';
  private q = '';
  private sel = new Set<string>();

  constructor(private el: HTMLElement, private h: NavHost) {
    el.addEventListener('click', (e) => this.onClick(e));
    el.addEventListener('change', (e) => this.onChange(e.target as HTMLInputElement));
    el.addEventListener('input', (e) => {
      const t = e.target as HTMLInputElement;
      if (t.id !== 'nq') return;
      this.q = t.value;
      this.render();
      const q = this.el.querySelector<HTMLInputElement>('#nq')!;
      q.focus();
      q.setSelectionRange(q.value.length, q.value.length);
    });
  }

  render() {
    const tabs = TABS.map(([k, n]) => `<button class="btn${this.tab === k ? ' on' : ''}" data-nt="${k}">${$t(n)}</button>`).join('');
    const body = this.tab === 'ter' ? this.terminals() : this.tab === 'con' ? this.connections() : this.tab === 'cab' ? this.cables() : this.devices();
    this.el.innerHTML = `<div class="reptabs">${tabs}<span class="spacer"></span>${this.tab === 'ter' ? '' : `<input class="search" id="nq" placeholder="${$t("Süz…")}" value="${esc(this.q)}" autocomplete="off" style="max-width:240px">`}</div>${body}`;
  }

  /* ---------------- terminal strips ---------------- */
  private strips(): Strip[] {
    return terminalStrips(this.h.A);
  }
  private current(): Strip | undefined {
    const all = this.strips();
    return all.find((s) => s.key === this.strip) ?? all[0];
  }
  private terminals(): string {
    const all = this.strips();
    if (!all.length) return `<h2>${$t("Klemens şeritleri")}</h2><div class="tblw"><div class="empty">${$t("Projede klemens yok.")}</div></div>`;
    const st = this.current()!;
    this.strip = st.key;
    const dup = duplicateNumbers(st.rows), acc = stripAccessories(st.rows), short = bridgeConflicts(this.h.pr, st.rows);
    const ro = this.h.readOnly ? ' disabled' : '';
    const chips = all.map((s) => `<button class="btn${s.key === st.key ? ' on' : ''}" data-ns="${esc(s.key)}">${esc(s.name)} <span class="note">${s.rows.length}</span></button>`).join('');
    const n = this.sel.size;
    return `<h2>${$t("Klemens şeridi {0}", esc(st.name))}</h2>
      <p class="sub">${$t("{0} klemens · {1} uç plakası · {2} köprü{3}{4}{5}", st.rows.length, acc.endPlates, acc.jumpers, acc.groups ? $t(" ({0} grup)", acc.groups) : '', dup.size ? ` · <span class="pill err">${$t("Tekrarlanan numara: {0}", esc([...dup].join(', ')))}</span>` : '', short.size ? ` · <span class="pill err">${$t("Kısa devre: {0} köprüsü farklı potansiyelleri birleştiriyor", esc([...short].join(', ')))}</span>` : '')}</p>
      <div class="btnrow" style="flex-wrap:wrap;margin-bottom:8px">${chips}</div>
      <div class="navbar">
        <button class="btn" data-nx="numsch"${ro} title="${$t("Klemensleri şemadaki yerlerine göre (sayfa, soldan sağa) 1'den numaralar")}">${$t("Şema sırasıyla numarala")}</button>
        <button class="btn" data-nx="numcur"${ro} title="${$t("Tablodaki sırayı koruyarak 1'den numaralar")}">${$t("Bu sırayla numarala")}</button>
        <span class="sep"></span>
        <span class="note">${n ? $t("{0} seçili:", n) : $t("Satır seçin:")}</span>
        <button class="btn" data-nx="bridge"${n < 2 || this.h.readOnly ? ' disabled' : ''} title="${$t("Seçili klemensleri köprüyle birleştir")}">${$t("Köprüle")}</button>
        <button class="btn" data-nx="unbridge"${!n || this.h.readOnly ? ' disabled' : ''}>${$t("Köprüyü kaldır")}</button>
        <input id="npart" placeholder="${$t("Parça no")}" style="width:130px"${ro}><button class="btn" data-nx="tpart"${!n || this.h.readOnly ? ' disabled' : ''}>${$t("Parça ata")}</button>
      </div>
      <div class="tblw"><table class="t nav"><thead><tr><th><input type="checkbox" data-nall aria-label="${$t("Tümünü seç")}"></th><th></th><th>${$t("No")}</th><th>${$t("İç bağlantı (üst)")}</th><th>${$t("Dış bağlantı (alt)")}</th><th>${$t("Potansiyel")}</th><th>${$t("Köprü")}</th><th>${$t("Parça no")}</th><th>${$t("Konum")}</th></tr></thead><tbody>
      ${st.rows
        .map((r, i) => {
          const id = r.it.s.id;
          return `<tr${this.sel.has(id) ? ' class="on"' : ''}><td><input type="checkbox" data-nsel="${id}"${this.sel.has(id) ? ' checked' : ''}></td>
          <td style="white-space:nowrap"><button class="btn mini" data-nup="${i}"${i === 0 || this.h.readOnly ? ' disabled' : ''} title="${$t("Yukarı")}">↑</button><button class="btn mini" data-ndn="${i}"${i === st.rows.length - 1 || this.h.readOnly ? ' disabled' : ''} title="${$t("Aşağı")}">↓</button></td>
          <td><input class="m${dup.has(r.no) ? ' bad' : ''}" data-nf="tno" data-nid="${id}" value="${esc(r.no)}" style="width:52px"${ro}></td>
          <td class="m">${esc(r.inner.join(', ')) || '<span class="note">—</span>'}</td><td class="m">${esc(r.outer.join(', ')) || '<span class="note">—</span>'}</td>
          <td class="m">${esc(r.pot)}</td>
          <td><input class="m${short.has(r.bridge) ? ' bad' : ''}" data-nf="tbr" data-nid="${id}" value="${esc(r.bridge)}" style="width:44px" placeholder="—"${ro}></td>
          <td><input class="m" data-nf="tpart" data-nid="${id}" value="${esc(r.part)}" style="width:110px"${ro}></td>
          <td><a href="#" class="m" data-ngo="${r.it.pi}|${id}">${esc(r.loc)}</a></td></tr>`;
        })
        .join('')}</tbody></table></div>
      <p class="note">${$t("Aynı köprü değerine sahip klemensler köprülenir (ör. 1). Sıra değiştirmek numaraları değiştirir; klemensin şemadaki yeri değişmez.")}</p>`;
  }

  /* ---------------- connections ---------------- */
  private connections(): string {
    const q = this.q.trim().toLocaleLowerCase('tr');
    const rows = netRows(this.h.pr, this.h.A).filter((n) => !q || `${n.no} ${n.pot} ${n.ends.join(' ')}`.toLocaleLowerCase('tr').includes(q));
    const ro = this.h.readOnly ? ' disabled' : '';
    const opt = (list: [string, string][] | string[], v: string) =>
      (v === '*' ? `<option value="*" selected>${$t("karışık")}</option>` : '') +
      list.map((o) => (Array.isArray(o) ? o : [o, o + ' mm²'])).map(([k, n]) => `<option value="${esc(k)}"${k === v ? ' selected' : ''}>${esc(n)}</option>`).join('');
    const cs: [string, string][] = [['', '—'], ...WIRE_SECTIONS.map((s): [string, string] => [s, s + ' mm²'])];
    const n = [...this.sel].filter((r) => rows.some((x) => x.root === r)).length;
    return `<h2>${$t("Bağlantılar")}</h2><p class="sub">${$t("{0} tel · Kesit ve renk bir teldeki bütün bağlantı parçalarına uygulanır.", rows.length)}</p>
      <div class="navbar"><span class="note">${n ? $t("{0} seçili:", n) : $t("Satır seçin:")}</span>
        <select id="ncs"${ro}>${opt(cs, '')}</select><select id="ncol"${ro}>${opt(WIRE_COLOURS, '')}</select>
        <button class="btn" data-nx="netset"${!n || this.h.readOnly ? ' disabled' : ''}>${$t("Seçilenlere uygula")}</button></div>
      <div class="tblw"><table class="t nav"><thead><tr><th><input type="checkbox" data-nall aria-label="${$t("Tümünü seç")}"></th><th>${$t("Tel")}</th><th>${$t("Potansiyel")}</th><th>${$t("Bağlı uçlar")}</th><th>${$t("Kesit")}</th><th>${$t("Renk")}</th><th>${$t("Sayfa")}</th></tr></thead><tbody>
      ${rows
        .map(
          (r) => `<tr${this.sel.has(r.root) ? ' class="on"' : ''}><td><input type="checkbox" data-nsel="${esc(r.root)}"${this.sel.has(r.root) ? ' checked' : ''}></td>
          <td class="m">${esc(r.no)}</td><td class="m">${esc(r.pot)}</td><td class="m" style="max-width:520px">${esc(r.ends.join(', ')) || `<span class="note">${$t("bağlantısız")}</span>`}</td>
          <td><select data-nf="ncs" data-nid="${esc(r.root)}"${ro}>${opt(cs, r.cs)}</select></td>
          <td><select data-nf="ncol" data-nid="${esc(r.root)}"${ro}>${opt(WIRE_COLOURS, r.col)}</select></td>
          <td>${r.pages.map((pi) => `<a href="#" data-npg="${pi}">${pi + 1}</a>`).join(' ')}</td></tr>`,
        )
        .join('')}</tbody></table></div>`;
  }

  /* ---------------- cables ---------------- */
  private cables(): string {
    const A = this.h.A, q = this.q.trim().toLocaleLowerCase('tr');
    const list = [...A.cables.values()]
      .map((c) => ({ c, name: A.devLabel.get(c.it.s.id) ?? c.it.s.tag }))
      .filter((x) => !q || `${x.name} ${x.c.it.s.props.cableType ?? ''}`.toLocaleLowerCase('tr').includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr', { numeric: true }));
    const ro = this.h.readOnly ? ' disabled' : '';
    const inp = (f: string, id: string, v: string | undefined, w: number, ph = '') => `<input class="m" data-nf="${f}" data-nid="${id}" value="${esc(v ?? '')}" style="width:${w}px" placeholder="${ph}"${ro}>`;
    return `<h2>${$t("Kablolar")}</h2><p class="sub">${$t("{0} kablo · Damarlar kablo tanım çizgisinin kestiği tellere sırayla atanır.", list.length)}</p>
      <div class="tblw"><table class="t nav"><thead><tr><th>${$t("Kablo")}</th><th>${$t("Tip")}</th><th>${$t("Damarlar")}</th><th>${$t("Kesit (mm²)")}</th><th>${$t("Uzunluk (m)")}</th><th>${$t("Kullanılan")}</th><th>${$t("Açıklama")}</th><th>${$t("Konum")}</th></tr></thead><tbody>
      ${list
        .map(({ c, name }) => {
          const s = c.it.s, used = c.cross.filter((x) => x.core).length, over = c.cross.length > c.cores.length;
          return `<tr><td class="m">${esc(name)}</td><td>${inp('ctype', s.id, s.props.cableType, 110, 'NYY-J')}</td><td>${inp('ccores', s.id, s.props.cores, 140, '1,2,3,PE')}</td>
            <td>${inp('ccs', s.id, s.props.cs, 56)}</td><td>${inp('clen', s.id, s.props.length, 60)}</td>
            <td class="m">${used}/${c.cores.length}${over ? ` <span class="pill err">${$t("damar yetmiyor")}</span>` : ''}</td>
            <td><input data-nf="cdesc" data-nid="${s.id}" value="${esc(s.desc)}" style="width:160px"${ro}></td>
            <td><a href="#" class="m" data-ngo="${c.it.pi}|${s.id}">/${esc(loc(c.it))}</a></td></tr>`;
        })
        .join('') || `<tr><td colspan="8"><div class="empty">${$t("Projede kablo yok.")}</div></td></tr>`}</tbody></table></div>`;
  }

  /* ---------------- devices (bulk editing) ---------------- */
  private devRows(): DeviceRow[] {
    const q = this.q.trim().toLocaleLowerCase('tr');
    return deviceRows(this.h.A).filter((d) => !q || `${d.name} ${d.main.s.desc} ${d.main.s.part} ${symbolName(d.main.s.type)}`.toLocaleLowerCase('tr').includes(q));
  }
  private devices(): string {
    const rows = this.devRows(), ro = this.h.readOnly ? ' disabled' : '';
    const n = [...this.sel].filter((k) => rows.some((r) => r.key === k)).length;
    const inp = (f: string, key: string, v: string | undefined, w: number, ph = '') => `<input data-nf="${f}" data-nid="${esc(key)}" value="${esc(v ?? '')}" style="width:${w}px" placeholder="${esc(ph)}"${ro}>`;
    return `<h2>${$t("Cihazlar")}</h2><p class="sub">${$t("{0} cihaz · Etiketi değiştirmek cihazın bütün kontaklarını da değiştirir. Birden çok satır seçip bir alanı topluca değiştirebilirsiniz.", rows.length)}</p>
      <div class="navbar"><span class="note">${n ? $t("{0} seçili:", n) : $t("Satır seçin:")}</span>
        <select id="nbf"${ro}>${DEV_FIELDS.map(([k, v]) => `<option value="${k}">${$t(v)}</option>`).join('')}</select>
        <input id="nbv" placeholder="${$t("Yeni değer")}" style="width:180px"${ro}>
        <button class="btn" data-nx="devset"${!n || this.h.readOnly ? ' disabled' : ''}>${$t("Seçilenlere uygula")}</button></div>
      <div class="tblw"><table class="t nav"><thead><tr><th><input type="checkbox" data-nall aria-label="${$t("Tümünü seç")}"></th><th>${$t("Etiket")}</th><th>${$t("Fonksiyon")}</th><th>${$t("Açıklama")}</th><th>${$t("Parça no")}</th><th>${$t("Üretici")}</th><th>=</th><th>++</th><th>+</th><th>#</th><th>${$t("Konumlar")}</th></tr></thead><tbody>
      ${rows
        .map((d) => {
          const s = d.main.s;
          return `<tr${this.sel.has(d.key) ? ' class="on"' : ''}><td><input type="checkbox" data-nsel="${esc(d.key)}"${this.sel.has(d.key) ? ' checked' : ''}></td>
            <td><input class="m" data-nf="dtag" data-nid="${esc(d.key)}" value="${esc(s.tag)}" style="width:70px"${ro}></td>
            <td>${esc(symbolName(s.type))}</td>
            <td>${inp('desc', d.key, s.desc, 200)}</td><td>${inp('part', d.key, s.part, 120)}</td><td>${inp('maker', d.key, s.maker, 110)}</td>
            <td>${inp('fn', d.key, s.fn, 60, this.h.pr.fn)}</td><td>${inp('site', d.key, s.site, 60, this.h.pr.site ?? '')}</td><td>${inp('loc', d.key, s.loc, 60, this.h.pr.loc)}</td><td>${inp('ud', d.key, s.ud, 50, this.h.pr.ud ?? '')}</td>
            <td class="m">${d.all.map((x) => `<a href="#" data-ngo="${x.pi}|${x.s.id}">/${esc(loc(x))}</a>`).join(' ')}</td></tr>`;
        })
        .join('') || `<tr><td colspan="11"><div class="empty">${$t("Cihaz bulunamadı.")}</div></td></tr>`}</tbody></table></div>`;
  }

  /* ---------------- events ---------------- */
  private visibleIds(): string[] {
    if (this.tab === 'ter') return this.current()?.rows.map((r) => r.it.s.id) ?? [];
    if (this.tab === 'con') {
      const q = this.q.trim().toLocaleLowerCase('tr');
      return netRows(this.h.pr, this.h.A).filter((n) => !q || `${n.no} ${n.pot} ${n.ends.join(' ')}`.toLocaleLowerCase('tr').includes(q)).map((n) => n.root);
    }
    if (this.tab === 'dev') return this.devRows().map((d) => d.key);
    return [];
  }

  private onClick(e: MouseEvent) {
    const t = e.target as HTMLElement;
    const d = (k: string) => t.closest<HTMLElement>(`[data-${k}]`)?.getAttribute(`data-${k}`);
    const tab = d('nt') as Tab | null;
    if (tab) {
      this.tab = tab;
      this.sel.clear();
      this.q = '';
      return this.render();
    }
    const ns = d('ns');
    if (ns != null) {
      this.strip = ns;
      this.sel.clear();
      return this.render();
    }
    const go = d('ngo');
    if (go) {
      e.preventDefault();
      const [pi, sid] = go.split('|');
      return this.h.focusOn(+pi, sid);
    }
    const pg = d('npg');
    if (pg != null) {
      e.preventDefault();
      return this.h.focusPage(+pg);
    }
    if (t.matches('[data-nsel]')) {
      const id = t.getAttribute('data-nsel')!;
      if ((t as HTMLInputElement).checked) this.sel.add(id);
      else this.sel.delete(id);
      return this.render();
    }
    if (t.matches('[data-nall]')) {
      const ids = this.visibleIds();
      if ((t as HTMLInputElement).checked) ids.forEach((i) => this.sel.add(i));
      else ids.forEach((i) => this.sel.delete(i));
      return this.render();
    }
    const up = d('nup') ?? d('ndn');
    if (up != null) {
      const st = this.current();
      if (!st) return;
      const i = +up, j = d('nup') != null ? i - 1 : i + 1;
      // swap the two numbers; keep the rest untouched
      const a = st.rows[i], b = st.rows[j];
      return this.h.mutate(() => {
        const na = a.no, nb = b.no;
        a.it.s.props = { ...a.it.s.props, pinNo: nb };
        b.it.s.props = { ...b.it.s.props, pinNo: na };
      });
    }
    const x = d('nx');
    if (x) this.act(x);
  }

  private act(x: string) {
    const st = this.current();
    if (x === 'numsch' && st) return this.h.mutate(() => renumberStrip(schematicOrder(st.rows)));
    if (x === 'numcur' && st) return this.h.mutate(() => renumberStrip(st.rows));
    const selRows = st?.rows.filter((r) => this.sel.has(r.it.s.id)) ?? [];
    if (x === 'bridge') {
      const used = new Set(st!.rows.map((r) => r.bridge).filter(Boolean));
      let b = 1;
      while (used.has(String(b))) b++;
      return this.h.mutate(() => selRows.forEach((r) => (r.it.s.props = { ...r.it.s.props, bridge: String(b) })));
    }
    if (x === 'unbridge')
      return this.h.mutate(() =>
        selRows.forEach((r) => {
          const p = { ...r.it.s.props };
          delete p.bridge;
          r.it.s.props = p;
        }),
      );
    if (x === 'tpart') {
      const pn = this.el.querySelector<HTMLInputElement>('#npart')!.value.trim();
      return this.h.mutate(() => selRows.forEach((r) => (r.it.s.part = pn)));
    }
    if (x === 'netset') {
      const cs = this.el.querySelector<HTMLSelectElement>('#ncs')!.value, col = this.el.querySelector<HTMLSelectElement>('#ncol')!.value;
      const nets = netRows(this.h.pr, this.h.A).filter((n) => this.sel.has(n.root));
      return this.h.mutate(() =>
        nets.forEach((n) =>
          n.wires.forEach(({ w }) => {
            if (cs) w.cs = cs;
            if (col) w.col = col;
          }),
        ),
      );
    }
    if (x === 'devset') {
      const f = this.el.querySelector<HTMLSelectElement>('#nbf')!.value, v = this.el.querySelector<HTMLInputElement>('#nbv')!.value.trim();
      const rows = deviceRows(this.h.A).filter((r) => this.sel.has(r.key));
      return this.h.mutate(() => rows.forEach((r) => this.setDevField(r, f, v)));
    }
  }

  private setDevField(r: DeviceRow, f: string, v: string) {
    if (f === 'fn' || f === 'loc' || f === 'site' || f === 'ud') {
      for (const it of r.all) {
        if (v) it.s[f] = v;
        else delete it.s[f];
      }
    } else if (f === 'desc' || f === 'part' || f === 'maker') r.main.s[f] = v;
  }

  private onChange(t: HTMLInputElement) {
    const f = t.dataset.nf, id = t.dataset.nid;
    if (!f || id == null) return;
    const v = t.value.trim(), A = this.h.A;
    const sym = () => A.all.find((x) => x.s.id === id)?.s;
    switch (f) {
      case 'tno':
      case 'tbr': {
        const s = sym();
        if (!s) return;
        return this.h.mutate(() => {
          const p = { ...s.props };
          if (f === 'tno') p.pinNo = v;
          else if (v) p.bridge = v;
          else delete p.bridge;
          s.props = p;
        });
      }
      case 'tpart': {
        const s = sym();
        return void (s && this.h.mutate(() => (s.part = v)));
      }
      case 'ncs':
      case 'ncol': {
        if (v === '*') return;
        const n = netRows(this.h.pr, A).find((x) => x.root === id);
        return void (n && this.h.mutate(() => n.wires.forEach(({ w }) => (f === 'ncs' ? (w.cs = v) : (w.col = v)))));
      }
      case 'ctype':
      case 'ccores':
      case 'ccs':
      case 'clen': {
        const s = sym();
        if (!s) return;
        const key = { ctype: 'cableType', ccores: 'cores', ccs: 'cs', clen: 'length' }[f] as 'cableType' | 'cores' | 'cs' | 'length';
        return this.h.mutate(() => (s.props = { ...s.props, [key]: key === 'cs' || key === 'length' ? v.replace(',', '.') : v }));
      }
      case 'cdesc': {
        const s = sym();
        return void (s && this.h.mutate(() => (s.desc = v)));
      }
      case 'dtag': {
        const r = deviceRows(A).find((x) => x.key === id);
        const tag = normaliseTag(v);
        if (!r || !tag || tag === r.main.s.tag) return this.render();
        const clash = deviceRows(A).find((x) => x.key !== id && x.main.s.tag === tag && (['fn', 'loc', 'site', 'ud'] as const).every((k) => (x.main.s[k] || '') === (r.main.s[k] || '')));
        if (clash) {
          toast($t("-{0} etiketi zaten kullanılıyor", tag));
          return this.render();
        }
        this.sel.delete(id);
        return this.h.mutate(() => renameDevice(r, tag));
      }
      default: {
        const r = deviceRows(A).find((x) => x.key === id);
        if (r && DEV_FIELDS.some(([k]) => k === f)) this.h.mutate(() => this.setDevField(r, f, v));
      }
    }
  }
}
